<template>
	<div class="wcPage">
		<loadingOverlay v-if="loading"></loadingOverlay>

		<div v-if="!loading && error">
			<o-notification type="info" variant="danger">
				{{ error }}
			</o-notification>
		</div>
		<div v-if="!loading && !error">
			<draggable
				v-model="sections"
				item-key="UUID"
				handle=".handle"
				:sort="isDraggable && isEditing"
				:key="updateKey"
				ghost-class="ghost-drag"
				class="columns is-multiline"
				:class="{ 'editing-mode': $store.getters.isEditing }"
				@change="Dragged"
				@start="StartDrag"
				@end="EndDrag">
				<template #item="{ element }">
					<div :class="ApplyColumnWidth(element)">
						<card :title="element.Title" :layout="CardLayout(element)" :src="sources[element.UUID]">
							<template #actions>
								<template v-if="!$store.getters.isEditing">
									<o-button
										variant="primary"
										inverted
										outlined
										v-if="CanShowGetReport(element)"
										@click="OpenGetReport(element)">
										{{ FullReportBtnText(element) }}
									</o-button>
									<o-button
										variant="primary"
										inverted
										outlined
										icon-left="external-link"
										v-if="element.ComponentType === 'box-files'"
										@click="OpenBoxFolder(element)">
										Open folder
									</o-button>
									<o-button
										variant="primary"
										inverted
										outlined
										icon-left="external-link"
										v-if="false && element.ComponentType === 'sharepoint-files'"
										@click="OpenSharepointFolder(element)">
										Open folder
									</o-button>
								</template>
								<template v-if="$store.getters.isEditing">
									<div class="adminButtons">
										<!-- <o-button
											size="small"
											variant="light"
											icon-left="save"
											:disabled="AdminButtonsDisabled(element)"
											@click="SavePageOnClick(element)">
										</o-button> -->
										<o-button
											variant="success"
											inverted
											:disabled="AdminButtonsDisabled(element)"
											icon-left="copy"
											@click="DuplicateSection(element)">
											Duplicate
										</o-button>
										<o-button
											variant="danger"
											:disabled="AdminButtonsDisabled(element)"
											icon-left="trash-alt"
											@click="ClickDelete(element)"></o-button>
									</div>
								</template>
							</template>
							<template #default>
								<wc-section-view
									:parent="uuid"
									:section="element"
									:filters="globalFilters"
									:editing="$store.getters.isEditing"
									@UpdateSection="UpdateSection"
									@promoPassBack="(o) => HandlePassBack(element, o)"></wc-section-view>
								<o-icon
									v-if="isDraggable && isEditing"
									icon="bars"
									size="medium"
									class="is-grabbable handle"
									style="margin-right: -0.5em"></o-icon>
							</template>
						</card>
					</div>
				</template>
			</draggable>
		</div>
		<div
			class="addSection my-5"
			v-if="$store.getters.isEditing && !loading && !error"
			@click="OpenAddSection">
			Add Widget
		</div>
		<save-bar
			:uuid="uuid"
			:vueRouterName="currentVueRouterName"
			v-if="isEditing"
			@Save="SavePageOnClick"
			@Revert="Revert"
			@Delete="DeletePage"
			@Editted="FinishedEdit"></save-bar>
	</div>
</template>

<script>
	import {
		getCurrentInstance,
		ref,
		computed,
		watch,
		onMounted,
		onBeforeMount,
		onBeforeUnmount,
		inject
	} from "vue";
	import { loadingOverlay } from "@/components";
	import editPageSectionsModal from "@/components/modals/editPageSectionsModal.vue";
	import draggable from "vuedraggable";
	import ContentType from "@/services/utils/contentTypes";
	import store from "@/store";
	import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
	import { refreshRoutes } from "@/services/routeSetup";
	import saveBar from "./wcPage/saveBar.vue";
	import pageData from "@/store/pageData";
	export default {
		components: {
			draggable,
			loadingOverlay,
			saveBar
		},
		props: {
			uuid: {
				type: [String, Number],
				default: undefined
			},
			view: {
				type: [String],
				default: "plain" //options: plain, card
			}
		},
		setup(props) {
			const self = getCurrentInstance();
			const $route = useRoute();
			const $router = useRouter();
			const $oruga = inject("$oruga");
			const $error = inject("$error");
			const $success = inject("$success");
			const $http = inject("$http");
			const $deepEquals = inject("$deepEquals");
			const $clean = inject("$clean");
			const $confirm = inject("$confirm");
			const moment = inject("$moment");
			const loading = ref(true);
			const error = ref();
			const body = ref({ Sections: [] });
			const uneditedBody = ref({ Sections: [] });
			const changed = ref(false);
			const sectionDeleted = ref(false);
			const updateKey = ref(0);

			const isAdmin = computed(() => {
				return store.getters.isAdmin;
			});

			const isEditing = computed(() => {
				return store.getters.isEditing;
			});

			const _saving = ref(false);
			const isSaving = computed(() => {
				return _saving.value;
			});

			const currentVueRouterName = computed(() => {
				return $route.name;
			});

			const globalFilters = computed(() => {
				return {
					ContentFilter: body.value.ContentFilter,
					UsersFilter: body.value.UsersFilter,
					AssignmentsFilter: body.value.AssignmentsFilter,
					CompaniesFilter: body.value.CompaniesFilter,
					Parent: {
						UsersFilter: body.value.Parent?.UsersFilter,
						AssignmentsFilter: body.value.Parent?.AssignmentsFilter,
						CompaniesFilter: body.value.Parent?.CompaniesFilter,
						ContentFilter: body.value.Parent?.ContentFilter
					}
				};
			});

			const isDraggable = ref(true);

			const sources = ref({});

			const normalizedBody = computed(() => {
				const bodyCopy = { ...body.value };
				try {
					bodyCopy.Sections = [];
					for (const section of body.value.Sections) {
						const normalizedSection = { ...section };
						if (normalizedSection.UUID && normalizedSection.UUID.startsWith("-")) {
							normalizedSection.UUID = null;
						}
						bodyCopy.Sections.push(normalizedSection);
					}
				} catch (e) {
					console.error(e);
				}
				return bodyCopy;
			});

			const sections = computed({
				get() {
					return body.value.Sections.filter((x) => x.IsDrawer == false);
				},
				set(newValue) {
					SetSections(newValue);
				}
			});

			const SetSections = (newValue, isDrawer) => {
				if (isDrawer) {
					const pageSections = body.value.Sections.filter((x) => x.IsDrawer == false);
					body.value.Sections = pageSections.concat(newValue);
				} else {
					const drawerSections = body.value.Sections.filter((x) => x.IsDrawer == true);
					body.value.Sections = newValue.concat(drawerSections);
				}
			};

			const AdminButtonsDisabled = (element) => {
				return _saving.value || element._saving || element._deleting;
			};

			const UpdateSection = (form) => {
				const section = body.value.Sections.find((o) => o.UUID === form.uuid);
				section.Width = form.result.width;
				section.Title = form.result.title;
				section.Text = form.result.text;
				section.Attributes = form.result.attrs;
				section.ToVueRouterName = form.result.to;
				changed.value = true;
			};

			const ClickDelete = async (element) => {
				if (confirm("You are about to remove this object. Are you sure?")) {
					const index = body.value.Sections.map((x) => x.UUID).indexOf(element.UUID);
					if (index < 0) {
						$error("UUID not found.");
					} else {
						body.value.Sections.splice(index, 1);
						changed.value = true;
					}
				}
			};

			const OpenBoxFolder = (element) => {
				let folderId = null;
				const attr = element.Attributes.find((x) => x.Name === "folder");
				if (attr) {
					try {
						folderId = JSON.parse(attr.Value)?.id;
					} catch (err) {
						console.error(err);
					}
					if (folderId) {
						window.open("https://boyden.app.box.com/folder/" + folderId, "_blank");
					} else {
						window.open("https://boyden.app.box.com/folder/" + "0", "_blank"); //Use the root id
						//$error("There is no folder selected.");
					}
				}
			};

			const OpenSharepointFolder = (element) => {
				let path = null;
				const attr = element.Attributes.find((x) => x.Name === "folder");
				if (attr) {
					try {
						path = JSON.parse(attr.Value)?.path;
					} catch (err) {
						console.error(err);
					}
					if (path) {
						window.open("https://boydenworld.sharepoint.com/sites/SP_Global_Boyden_Assets/" + path, "_blank");
					} else {
						window.open("https://boydenworld.sharepoint.com/sites/SP_Global_Boyden_Assets/", "_blank"); //Use the root id
						//$error("There is no folder selected.");
					}
				}
			};

			const CardLayout = (element) => {
				switch (element.ComponentType) {
					case "card-table":
					case "recent-assignments":
					case "member-list":
					case "recent-companies":
						return "table";
					default:
						return "";
				}
			};

			const FullReportBtnText = (element) => {
				const fullReportBtnText = element?.Attributes?.find(
					(el) => el.Name == "fullReportBtnText"
				)?.Value;
				return fullReportBtnText || "Full report";
			};

			const CanShowGetReport = (element) => {
				switch (element.ComponentType) {
					case "dashboard-promo":
					case "dashboard":
					case "recent-assignments":
					case "recent-companies":
						//case "promos":
						return true;
					case "member-list":
					case "member-list-promo":
					case "new-partners": {
						const showFullReport =
							element.Attributes.find((el) => el.Name == "fullReport")?.Value == "true";
						return showFullReport;
					}
					default:
						return false;
				}
			};
			const GetReportFilter = (element) => {
				const attr = element.Attributes?.find((x) => x.Name === "json");

				if (attr) {
					return JSON.parse(attr?.Value || "[]");
				} else {
					if (element.ComponentType === "dashboard" || element.ComponentType === "dashboard-promo") {
						let assnFilter = body.value.AssignmentsFilter || [];
						if (assnFilter.length === 0) {
							assnFilter = body.value.Parent?.AssignmentsFilter || [];
						}
						return assnFilter;
					}
					//const usersFilter = body.value.UsersFilter;
					if (element.ComponentType === "recent-assignments") {
						let assnFilter = body.value.AssignmentsFilter || [];
						if (assnFilter.length === 0) {
							assnFilter = body.value.Parent?.AssignmentsFilter || [];
						}
						return assnFilter;
					}
					if (element.ComponentType === "recent-companies") {
						let companyFilter = body.value.CompaniesFilter || [];
						if (companyFilter.length === 0) {
							companyFilter = body.value.Parent?.CompaniesFilter || [];
						}
						return companyFilter;
					}
					if (
						element.ComponentType === "new-partners" ||
						element.ComponentType === "member-list" ||
						element.ComponentType === "member-list-promo"
					) {
						let userFilter = body.value.UsersFilter || [];
						if (userFilter.length === 0) {
							userFilter = body.value.Parent?.UsersFilter || [];
						}
						return userFilter;
					}

					return [];
				}
			};
			const OpenGetReport = (element) => {
				const filters = GetReportFilter(element);
				let filterDates = {
					startDate: moment.utc().subtract(1, "year").toDate(),
					endDate: moment.utc().toDate(),
					selection: "1Y"
				};

				switch (element.ComponentType) {
					case "dashboard-promo": {
						filterDates = {
							startDate: moment.utc().subtract(1, "year").startOf("quarter").toDate(),
							endDate: moment.utc().subtract(3, "months").endOf("quarter").toDate(),
							selection: "Custom"
						};
						store.dispatch("setParams", {
							dates: filterDates,
							keys: filters
						});
						$router.push({
							name: "_Dashboard"
						});
						return true;
					}
					case "dashboard": {
						store.dispatch("setParams", {
							dates: filterDates,
							keys: filters
						});
						$router.push({
							name: "_Dashboard"
						});
						return true;
					}
					case "recent-assignments": {
						store.dispatch("setParams", {
							dates: filterDates,
							keys: filters
						});
						$router.push({
							name: "_ReportsAssignments"
						});
						return true;
					}
					case "recent-companies": {
						store.dispatch("setParams", {
							dates: filterDates,
							keys: filters
						});
						$router.push({
							name: "_ReportsCompanies"
						});
						return true;
					}
					case "promos": {
						$router.push({ name: "_Dashboard" });
						return true;
					}
					case "member-list":
					case "member-list-promo":
					case "new-partners": {
						console.log("setParams:", { dates: filterDates, keys: filters });
						store.dispatch("setParams", {
							dates: filterDates,
							keys: filters,
							autoRun: true
						});
						$router.push({
							name: "_ReportsUsers"
						});
						return true;
					}
				}
			};

			const GetPageHtml = async () => {
				loading.value = true;
				try {
					const response = await $http.get("/api/v2/page/" + props.uuid);
					if (!response?.data) throw new Error("The page returned no content.");

					body.value = response.data;
					uneditedBody.value = JSON.parse(JSON.stringify(response.data));
					pageData.page.value = response.data;
				} catch (err) {
					console.error("Unable to load page content.", err);
					error.value = err?.response?.data || err?.message || "Unable to load page content.";
				} finally {
					loading.value = false;
				}
			};

			const GetPageSections = async () => {
				try {
					const response = await $http.get("/api/v2/page/" + props.uuid);
					return response.data.Sections;
				} catch (err) {
					console.error(err);
					return [];
				}
			};
			const RefreshPageSections = async () => {
				const sections = await GetPageSections();
				body.value.Sections = sections;
				uneditedBody.value.Sections = JSON.parse(JSON.stringify(sections));
			};

			const HandlePassBack = async (element, o) => {
				if (o.image?.src) {
					sources.value[element.UUID] = o.image.src;
				}
			};

			const SetSrc = (element, src) => {
				sources.value[element.UUID] = src;
			};

			const UpToDateOrConfirm = async () => {
				const sections = await GetPageSections();
				const copy1 = JSON.parse(JSON.stringify(uneditedBody.value.Sections));
				const copy2 = JSON.parse(JSON.stringify(sections));
				$clean(copy1, "OID", "DOID");
				$clean(copy2, "OID", "DOID");
				const different = !$deepEquals(copy1, copy2);

				if (different) {
					if (!confirm("There are recent changes to this page that will be overwritten. Continue?")) {
						return false;
					}
				}
				return true;
			};

			const SavePageOnClick = async (result) => {
				if (sectionDeleted.value && !confirm("You are about to remove this object. Are you sure?")) {
					return false;
				}
				if (!(await UpToDateOrConfirm())) return false;
				if (!(await SavePage(false, result.saving))) return false;
				await RefreshPageSections();
				return true;
			};

			const SavePage = async (suppressMessage, saving) => {
				const payload = normalizedBody.value;
				const incompleteImage = payload.Sections.some((section) => {
					const html = new DOMParser().parseFromString(section.Text || "", "text/html");
					return Array.from(html.querySelectorAll("img")).some((image) =>
						image.hasAttribute("data-ck-upload-id") || !image.getAttribute("src")?.trim()
					);
				});
				if (incompleteImage) {
					$error("An image is not ready to save. Wait for the upload to finish. If it remains missing, remove it and insert it again.");
					return false;
				}
				saving.value = true;
				try {
					await $http.post("/api/v2/page", payload);
					if (!suppressMessage) {
						$success("Saved!");
					}
					changed.value = false;
					sectionDeleted.value = false;
					return true;
				} catch (err) {
					console.error("Unable to save page content.", err);
					error.value = err?.response?.data || err?.message || "Unable to save page content.";
					return false;
				} finally {
					saving.value = false;
				}
			};

			const Revert = () => {
				body.value = JSON.parse(JSON.stringify(uneditedBody.value));
				changed.value = false;
				updateKey.value = Math.random();
			};

			const DeletePage = async () => {
				try {
					await $http.delete("/api/v2/page", { data: { uuid: props.uuid } });
					const path = $route.path.split("/");
					const parentPath = path.slice(0, path.length - 1);
					$router.push(parentPath.join("/"));
					refreshRoutes();
				} catch (err) {
					//error.value = err.response.data;
					console.error(err);
				}
			};

			const FinishedEdit = (result) => {
				refreshRoutes();
				GetPageHtml(); //Refresh
			};

			const OpenAddSection = (isDrawer = false) => {
				$oruga.modal.open({
					parent: self,
					component: editPageSectionsModal,
					trapFocus: true,
					events: {
						add: (result) => {
							try {
								//await RefreshPageSections(); //Helps to avoid multi-user modification of the same page.
								const section = {
									UUID: (-Math.floor(Math.random() * 100000)).toString(),
									Title: result.section.title,
									Width: result.section.columnWidth,
									Type: {
										UUID: ContentType.GetId(result.section.contentType),
										Identifier: result.section.contentType
									},
									IsDrawer: isDrawer === true
								};
								if (result.section.contentType === ContentType.RichText) {
									section.Text = "";
								}
								if (result.section.contentType === ContentType.VueComponent) {
									section.ComponentType = result.section.componentType;
								}
								if (result.section.contentType === ContentType.PagePromotion) {
									section.ToVueRouterName = result.section.toVueRouterName;
								}
								body.value.Sections.push(section);
								changed.value = true;
								// await SavePage();
								// GetPageHtml();
								result.close();
							} catch (err) {
								console.error(err);
								$error("A problem occurred.");
							}
						}
					}
				});
			};

			const DuplicateSection = (section) => {
				const newSection = { ...section };
				newSection.UUID = (-Math.floor(Math.random() * 100000)).toString(); //must be normalized to 0 before submit
				delete newSection.OID;
				delete newSection.DOID;

				const index = body.value.Sections.findIndex((o) => {
					return o.UUID === section.UUID;
				});
				body.value.Sections.splice(index + 1, 0, newSection);
				changed.value = true;
			};

			const ApplyColumnWidth = (section) => {
				const classes = [
					{ column: true },
					{ "is-1": section.Width === 1 },
					{ "is-2": section.Width === 2 },
					{ "is-3": section.Width === 3 },
					{ "is-4": section.Width === 4 },
					{ "is-5": section.Width === 5 },
					{ "is-12 is-6-widescreen": section.Width === 6 },
					{ "is-7": section.Width === 7 },
					{ "is-8": section.Width === 8 },
					{ "is-9": section.Width === 9 },
					{ "is-10": section.Width === 10 },
					{ "is-11": section.Width === 11 },
					{ "is-12": section.Width === 12 }
				];
				return classes;
			};

			const Dragged = () => {
				changed.value = true;
			};

			const StartDrag = (e) => {
				store.dispatch("setDragState", true);
				return false;
			};
			const EndDrag = (e) => {
				store.dispatch("setDragState", false);
				return false;
			};

			const SetApi = () => {
				pageData.api.value = {
					OpenAddSection: OpenAddSection,
					ClickDelete: ClickDelete,
					SetSections: SetSections
				};
			};

			onMounted(async () => {
				SetApi();

				if (props.uuid && props.uuid !== "") {
					await GetPageHtml();
				} else {
					error.value = "Missing PageId.";
					loading.value = false;
				}
			});

			const PromptUnsavedChanges = async () => {
				if (changed.value) {
					return new Promise((resolve, reject) => {
						$confirm({
							title: "Unsaved Changes",
							message: "There are unsaved changes. Continue to destination?",
							onCancel: () => {
								resolve(false);
							},
							onConfirm: () => {
								resolve(true);
							}
						});
					});
				} else {
					return new Promise((resolve) => {
						resolve(true);
					});
				}
			};

			const redirectUrl = computed(() => {
				return $route.meta.redirectLink;
			});

			const TryRedirect = () => {
				if (redirectUrl.value.startsWith("http")) {
					document.location.assign(redirectUrl.value);
				} else {
					if (redirectUrl.value.startsWith("/")) {
						$router.push({ path: redirectUrl.value });
					} else {
						$router.push({ name: redirectUrl.value });
					}
				}
			};

			const beforeunloadFn = (e) => {
				if (changed.value) {
					const message = "There are unsaved changes. Are you sure?";
					e.returnValue = message;
					return message;
				}
			};
			onBeforeMount(() => {
				if (redirectUrl.value && redirectUrl.value.length > 0) {
					TryRedirect();
					return;
				}
				addEventListener("beforeunload", beforeunloadFn);
			});
			onBeforeUnmount(() => {
				try {
					removeEventListener("beforeunload", beforeunloadFn);
				} catch (err) {
					console.error(err);
				}
			});

			onBeforeRouteLeave(async (to, from) => {
				return await PromptUnsavedChanges();
			});

			return {
				loading,
				error,
				body,
				updateKey,
				sources,
				changed,
				globalFilters,
				isDraggable,
				isAdmin,
				isEditing,
				isSaving,
				currentVueRouterName,
				sections,
				AdminButtonsDisabled,
				UpdateSection,
				ClickDelete,
				CardLayout,
				GetPageHtml,
				HandlePassBack,
				SavePageOnClick,
				Revert,
				DeletePage,
				FinishedEdit,
				OpenAddSection,
				DuplicateSection,
				ApplyColumnWidth,
				Dragged,
				StartDrag,
				EndDrag,
				OpenBoxFolder,
				OpenSharepointFolder,
				CanShowGetReport,
				OpenGetReport,
				FullReportBtnText
			};
		}
	};
</script>

<style lang="scss" scoped>
	@use "@/assets/css/globalVars.scss" as *;

	.wcPage {
		& .addSection {
			padding: 2em;
			border: 1px dashed #000;
			max-width: 240px;
			text-align: center;

			&:hover {
				background-color: $color-background-dark;
				cursor: pointer;
			}
		}

		& .column .prime-card {
			position: relative;
			height: 100%;
		}

		& .columns.editing-mode > .column {
			height: 100%;
		}
	}

	.handle {
		position: absolute;
		left: 0;
		top: 50%;
		transform: translate(-50%, -50%);
		border: 1px solid $color-background;
		border-radius: $radius-medium;
		background-color: white;
		color: #929099;
	}

	.adminButtons {
		display: inherit;
		gap: inherit;

		// outline: 2px solid $color-border-hr;
		// border: $margin-element-tiny solid transparent;
		// box-sizing: border-box;
		// border-radius: $radius-small;
	}
</style>
